import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useLayoutEffect, useMemo } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { getFormatInstance } from '@sas-dvr/ltjs-sasicu/util/SASFormatterUtil';
import { Slider, RangeSlider, BaseSlider, } from '@sas/nova-commons/components/Slider';
import { DateSlider } from '@sas/nova-commons/components/Slider/DateSlider';
import { useDesignTokens } from '@sas/nova-commons/theme';
import { setConstraints } from '../../store/birdLayout/BirdLayoutState';
import { SliderState } from '../../store/birdElement/sliderState';
import { selectBirdSliderState, selectStyleState, selectAccessibilityLabelState, } from '../../store/selectors';
import { longestTextWidth, fontStyles, textHeight } from '../utils/style';
import { useFontLoadedKey } from '../FontProvider';
import { parseString } from '../utils/format';
import { useI18n } from '../../locale/I18nContext';
import { createDateTimeSliderStepReducer, getDateTimeSteps, getSasDateValueType, getSasDateValueTypeEncoder, getDateTimeFormatParts, } from './BirdSliderSteps';
import { createStepsForNumericRange } from './bestFormatUtil';
import { useFormatLocale } from '@sas/nova-commons/locale/formatting';
import { clampValue, roundDateForFormat, roundRangeForFormat } from './formatUtil';
import { Checkbox } from '@sas/nova-commons/components/Checkbox';
const errorMessageLocalKey = {
    key: 'TextFieldTypeAhead.validation.numeric.txt',
};
export const BirdSliderBase = function (props) {
    var _a;
    const { actions, roundRange } = props;
    const { range, format, sliderState, horizontal, missingValueBehavior, missingValueCheckboxLocation, } = props.state;
    const formatLocale = useFormatLocale();
    const localize = useI18n();
    const dt = useDesignTokens();
    const [formatter, resolvedFormat] = useMemo(() => {
        // getFormatInstance could return null, but never will in a BIRD scenario
        // TODO: add dev warning
        const instance = getFormatInstance(format || 'BEST6', formatLocale);
        if (instance) {
            return [instance, format || 'BEST6'];
        }
        return [
            getFormatInstance('BEST6', formatLocale),
            'BEST6',
        ];
    }, [format, formatLocale]);
    const dateValueType = getSasDateValueType(formatter);
    const errorMsg = useI18n(errorMessageLocalKey);
    const snapValue = useMemo(() => {
        return (value) => {
            value = clampValue(value, formatter);
            // 1) format the value so that the formatter can round the value
            // 2) parse the format to get the value
            // Without more information this is the only way to generically snap.
            if (dateValueType === undefined) {
                return formatter.parse(formatter.format(value));
            }
            const isDate = formatter === null || formatter === void 0 ? void 0 : formatter.isDateFormat();
            return roundDateForFormat(value, isDate, resolvedFormat);
        };
    }, [formatter, dateValueType, resolvedFormat]);
    const { maxRange, minRange } = useMemo(() => {
        if (!range) {
            return {
                minRange: 0,
                maxRange: 100,
            };
        }
        const max = clampValue(range.max, formatter);
        const min = clampValue(range.min, formatter);
        if (!roundRange) {
            return { minRange: snapValue(min), maxRange: snapValue(max) };
        }
        const roundedRange = roundRangeForFormat(min, max, resolvedFormat);
        return {
            minRange: roundedRange.min,
            maxRange: roundedRange.max,
        };
    }, [formatter, range, resolvedFormat, roundRange, snapValue]);
    const sliderFormatter = useMemo(() => {
        const ltjsFormatter = getFormatInstance('BEST32.', formatLocale);
        return {
            format(value) {
                if (!snapValue)
                    return '';
                return formatter.format(snapValue(value)) || '';
            },
            formatEditText(value) {
                var _a;
                if (!snapValue)
                    return '';
                return (_a = ltjsFormatter === null || ltjsFormatter === void 0 ? void 0 : ltjsFormatter.format(snapValue(value))) !== null && _a !== void 0 ? _a : '';
            },
            parse(inputText) {
                if (!snapValue)
                    return { type: 'success', result: 0 };
                const parseResult = parseString(inputText, formatLocale);
                if (isNaN(parseResult)) {
                    return { type: 'error', result: errorMsg };
                }
                else {
                    return { type: 'success', result: snapValue(parseResult) };
                }
            },
        };
    }, [errorMsg, snapValue, formatter, formatLocale]);
    {
        const styleOverrideFont = (_a = props.style) === null || _a === void 0 ? void 0 : _a.font;
        const dt = useDesignTokens();
        const fonts = useMemo(() => fontStyles(styleOverrideFont, dt), [dt, styleOverrideFont]);
        const fontKey = useFontLoadedKey(fonts.fontFamily);
        useLayoutEffect(() => {
            function getSliderWidthForValue(value) {
                if (value === undefined) {
                    return 0;
                }
                else if (dateValueType === undefined) {
                    value = parseFloat(value.toString().replace(/\d/g, '8'));
                }
                // this holds the amount of width in px that the control takes up without using text. In the horizontal
                // case, it's the padding on the text label or the extra width of the grip input control. The vertical
                // case is the same with the added width of the slider track.
                let controlWidth;
                // the text measurement we do here isn't really perfect. We only measure values at the end/beginning
                // of the range, but there may be values in the middle that have a longer length. This is also font
                // and locale dependant in some cases (date formatting). This factor is used to add extra width to
                // handle these cases while not adding too much for less extreme cases.
                let textFudgeFactor;
                if (dateValueType === undefined) {
                    controlWidth = horizontal
                        ? 24 /* grip width */ + 32 /* grip label padding */
                        : 24 /* grip width */ + 32 /* grip label padding */ + 4 /* tick label padding */;
                    textFudgeFactor = horizontal ? 1.25 : 1.1;
                }
                else {
                    controlWidth = horizontal
                        ? 24 /* grip width */ + 64 /* grip label padding */
                        : 24 /* grip width */ + 49 /* grip label padding */ + 4 /* tick label padding */;
                    textFudgeFactor = horizontal ? 1.25 : 1.2;
                }
                let longestText = sliderFormatter.format(value);
                if (longestText === '') {
                    return controlWidth;
                }
                // FIXME(maybe?): horizontal slider does not always have a range (so this is over estimating the width).
                // FIXME: localize.
                longestText = horizontal ? `${longestText} to ${longestText}` : longestText;
                const textWidth = longestTextWidth([longestText], fonts);
                // Tick labels contribute to width for vertical sliders.
                // tickLabelWidth doesn't need the fudge factor because its label was used to calculate textWidth.
                const tickLabelWidth = horizontal ? 0 : textWidth;
                const gripLabelWidth = textWidth * textFudgeFactor;
                return tickLabelWidth + gripLabelWidth + controlWidth;
            }
            const preferredWidth = Math.max(getSliderWidthForValue(minRange), getSliderWidthForValue(maxRange));
            const minWidth = Math.max(preferredWidth, props.preferredSizing ? 200 : 160);
            let height;
            if (horizontal) {
                const labelHeight = textHeight(fonts);
                const missingValuesControlHeight = Math.max(labelHeight, 25 /* checkbox height */);
                height =
                    4 + // padding
                        labelHeight + // grip labels
                        4 + // padding
                        4 + // margin
                        4 + // row spacing
                        8 + // track
                        4 + // row spacing
                        labelHeight + // tick labels
                        8 + // margin
                        (missingValueCheckboxLocation === 'bottom' ? missingValuesControlHeight : 0); // missing values checkbox
            }
            else {
                height = 200;
            }
            actions.setConstraints(props.id, {
                minHeight: height,
                preferredHeight: height,
                minWidth: minWidth,
                preferredWidth: preferredWidth,
            });
        }, [
            fontKey /*(implicit)*/,
            fonts,
            sliderFormatter,
            dateValueType,
            minRange,
            maxRange,
            actions,
            horizontal,
            props.id,
            props.preferredSizing,
            missingValueCheckboxLocation,
        ]);
    }
    const [step, stepMedium, stepLarge] = useMemo(() => {
        const formatName = formatter.getFormatName();
        if (!formatName) {
            return [undefined, undefined, undefined];
        }
        else if (dateValueType !== undefined) {
            const { toJsDate, fromJsDate } = getSasDateValueTypeEncoder(dateValueType);
            return getDateTimeSteps(formatName, formatter.getWidth()).map((dateTimeStep) => {
                const reducer = createDateTimeSliderStepReducer(dateValueType, dateTimeStep);
                return (prevValue, action) => toJsDate(reducer(fromJsDate(prevValue), action));
            });
        }
        else if (!range) {
            return [undefined, undefined, undefined];
        }
        else {
            return createStepsForNumericRange(formatter, [minRange, maxRange]);
        }
    }, [formatter, dateValueType, minRange, maxRange, range]);
    const labelTokens = {
        theme: () => props.style && props.style.normal && { fg: props.style.normal.fg },
        font: props.style && props.style.font,
    };
    const commonSliderProps = {
        testId: `${props.id}-slider`,
        style: props.preferredSizing && !horizontal ? { height: '200px' } : undefined,
        orientation: horizontal ? 'horizontal' : 'vertical',
        tickLabelTokens: labelTokens,
        handleLabelTokens: labelTokens,
        ariaLabel: props.accessibilityLabel,
        disableRoundToStep: true,
    };
    if (!sliderState) {
        // Early return so the following logic can depend on having a defined slider value.
        // We can't render a Slider without a value, so this has to use BaseSlider.
        return _jsx(BaseSlider, Object.assign({}, commonSliderProps, { values: [] }));
    }
    const missingValuesControl = missingValueCheckboxLocation === 'bottom' && (_jsx(Checkbox, { checked: missingValueBehavior === 'include' || false, onPress: () => {
            actions.update(props.id, {
                missingValueBehavior: missingValueBehavior === 'include' ? 'default' : 'include',
            });
        }, label: localize({ key: 'Slider.missingValues.txt' }), theme: () => {
            var _a, _b, _c;
            return { '--checkbox-label-fg': (_c = (_b = (_a = props.style) === null || _a === void 0 ? void 0 : _a.normal) === null || _b === void 0 ? void 0 : _b.fg) !== null && _c !== void 0 ? _c : dt.reportTokens.text.basic.fg };
        } }));
    let sliderValue;
    if (sliderState.max !== undefined) {
        sliderValue = {
            type: 'range',
            minValue: sliderState.min,
            maxValue: sliderState.max,
            onChange(e) {
                actions.update(props.id, { sliderState: { min: e.minValue, max: e.maxValue } });
            },
            onCommit(e) {
                if (!snapValue)
                    return;
                actions.update(props.id, {
                    values: { min: snapValue(e.minValue), max: snapValue(e.maxValue) },
                });
            },
        };
    }
    else {
        sliderValue = {
            type: 'value',
            value: sliderState.min,
            onChange(e) {
                actions.update(props.id, { sliderState: { min: e.value } });
            },
            onCommit(e) {
                if (!snapValue)
                    return;
                actions.update(props.id, { values: { min: snapValue(e.value) } });
            },
        };
    }
    if (dateValueType !== undefined) {
        const { toJsDate, fromJsDate } = getSasDateValueTypeEncoder(dateValueType);
        let dateSliderProps;
        if (sliderValue.type === 'range') {
            dateSliderProps = {
                type: sliderValue.type,
                minValue: toJsDate(sliderValue.minValue),
                maxValue: toJsDate(sliderValue.maxValue),
                onChange(e) {
                    sliderValue.onChange({
                        minValue: fromJsDate(e.minValue),
                        maxValue: fromJsDate(e.maxValue),
                    });
                },
                onCommit(e) {
                    var _a, _b;
                    (_b = (_a = sliderValue).onCommit) === null || _b === void 0 ? void 0 : _b.call(_a, {
                        minValue: fromJsDate(e.minValue),
                        maxValue: fromJsDate(e.maxValue),
                    });
                },
            };
        }
        else {
            dateSliderProps = {
                type: sliderValue.type,
                value: toJsDate(sliderValue.value),
                onChange(e) {
                    sliderValue.onChange({ value: fromJsDate(e.value) });
                },
                onCommit(e) {
                    var _a, _b;
                    (_b = (_a = sliderValue).onCommit) === null || _b === void 0 ? void 0 : _b.call(_a, { value: fromJsDate(e.value) });
                },
            };
        }
        const scale = range
            ? [toJsDate(minRange), toJsDate(maxRange)]
            : [new Date(2000, 0, 1), new Date(2020, 11, 31)];
        return (_jsxs(Fragment, { children: [_jsx(DateSlider, Object.assign({}, commonSliderProps, dateSliderProps, getDateTimeFormatParts(formatter), { scale: scale, ticks: scale, format: (value) => sliderFormatter.format(fromJsDate(value)), step: step, stepMedium: stepMedium, stepLarge: stepLarge })), missingValuesControl] }));
    }
    else {
        // needed for input validation
        const finalRange = [minRange, maxRange];
        const numericSliderProps = {
            ticks: finalRange,
            scale: finalRange,
            format: sliderFormatter,
            step: step,
            stepMedium: stepMedium,
            stepLarge: stepLarge,
        };
        if (sliderValue.type === 'range') {
            return (_jsxs(Fragment, { children: [_jsx(RangeSlider, Object.assign({}, commonSliderProps, numericSliderProps, sliderValue)), missingValuesControl] }));
        }
        else {
            return (_jsxs(Fragment, { children: [_jsx(Slider, Object.assign({}, commonSliderProps, numericSliderProps, sliderValue)), missingValuesControl] }));
        }
    }
};
export const BirdSlider = connect((state, props) => ({
    state: selectBirdSliderState(state, props.id),
    style: selectStyleState(state, props.id),
    accessibilityLabel: selectAccessibilityLabelState(state, props.id),
    roundRange: state.transport.roundBirdSliderRange,
}), (dispatch) => ({
    actions: bindActionCreators(Object.assign({ setConstraints }, SliderState.actions), dispatch),
}))(BirdSliderBase);
